<template>
  <div class="rounded-lg border bg-card p-6">
    <h3 class="text-base font-semibold text-foreground">{{ $ts('checkout.paymentMethod.title') }}</h3>

    <div class="mt-4 space-y-3">
      <!-- Pay online (Payment Drop-in) -->
      <label
        :class="[
          'flex cursor-pointer items-start gap-3 rounded-lg border p-4 transition-colors',
          modelValue === 'online'
            ? 'border-primary/50 bg-primary/5'
            : 'border-input hover:bg-accent/50',
        ]"
      >
        <input
          type="radio"
          name="paymentMethod"
          value="online"
          :checked="modelValue === 'online'"
          class="mt-0.5 h-4 w-4 accent-primary"
          @change="emit('update:modelValue', 'online')"
        />
        <div>
          <span class="text-sm font-medium text-foreground">{{ $ts('checkout.paymentMethod.payOnline') }}</span>
          <p class="text-sm text-muted-foreground">{{ $ts('checkout.paymentMethod.payOnlineDescription') }}</p>
        </div>
      </label>

      <!-- Pay in-store -->
      <label
        :class="[
          'flex cursor-pointer items-start gap-3 rounded-lg border p-4 transition-colors',
          modelValue === 'venue'
            ? 'border-primary/50 bg-primary/5'
            : 'border-input hover:bg-accent/50',
        ]"
      >
        <input
          type="radio"
          name="paymentMethod"
          value="venue"
          :checked="modelValue === 'venue'"
          class="mt-0.5 h-4 w-4 accent-primary"
          @change="emit('update:modelValue', 'venue')"
        />
        <div>
          <span class="text-sm font-medium text-foreground">{{ $ts('checkout.paymentMethod.payInStore') }}</span>
          <p class="text-sm text-muted-foreground">{{ $ts('checkout.paymentMethod.payInStoreDescription') }}</p>
        </div>
      </label>
    </div>

    <!-- Payment Drop-in container (shown when "online" is selected) -->
    <div v-if="modelValue === 'online'" class="mt-5">
      <!-- Loading state -->
      <div v-if="adyenLoading" class="flex items-center justify-center py-8">
        <div class="flex items-center gap-2 text-sm text-muted-foreground">
          <Icon name="tabler:loader-2" size="18" class="animate-spin" />
          {{ $ts('checkout.paymentMethod.loadingPayment') }}
        </div>
      </div>

      <!-- Error state -->
      <div v-if="adyenError && !adyenLoading" class="rounded-md border border-destructive/50 bg-destructive/10 p-3">
        <div class="flex items-start gap-2">
          <Icon name="tabler:alert-circle" size="16" class="mt-0.5 shrink-0 text-destructive" />
          <div>
            <p class="text-sm font-medium text-destructive">
              {{ $ts('checkout.paymentMethod.paymentError') }}
            </p>
            <p class="mt-1 text-xs text-destructive/80">{{ adyenError }}</p>
          </div>
        </div>
      </div>

      <!-- Payment Drop-in mount point -->
      <div
        ref="adyenContainerRef"
        class="adyen-dropin-wrapper"
      />
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref, watch, nextTick, onBeforeUnmount } from 'vue';
import { useI18n } from '#imports';
import { useCheckout } from '#composables/useCheckout';

const { $ts } = useI18n();

const props = defineProps<{
  modelValue: 'online' | 'venue';
  orderId?: string;
}>();

const emit = defineEmits<{
  'update:modelValue': [value: 'online' | 'venue'];
  'payment-completed': [resultCode: string];
  'payment-failed': [resultCode: string];
}>();

const adyenContainerRef = ref<HTMLElement | null>(null);

// Only initialize checkout when we have an orderId and card is selected
const adyenLoading = ref(false);
const adyenError = ref<string | null>(null);
let checkoutDestroy: (() => void) | null = null;

async function mountPaymentDropin() {
  if (!props.orderId || !adyenContainerRef.value) return;

  // Clean up any previous instance
  if (checkoutDestroy) {
    checkoutDestroy();
    checkoutDestroy = null;
  }

  const returnUrl = `${window.location.origin}/checkout/result`;

  const { initCheckout, destroy, loading, error } = useCheckout({
    orderId: props.orderId,
    returnUrl,
    onPaymentCompleted: (resultCode) => {
      emit('payment-completed', resultCode);
    },
    onPaymentFailed: (resultCode) => {
      emit('payment-failed', resultCode);
    },
    onError: (err) => {
      adyenError.value = err.message;
    },
  });

  checkoutDestroy = destroy;
  adyenLoading.value = true;
  adyenError.value = null;

  await initCheckout(adyenContainerRef.value as unknown as HTMLElement);

  adyenLoading.value = loading.value;
  adyenError.value = error.value;
}

// Watch for when card is selected AND we have an orderId — mount the Drop-in
watch(
  () => [props.modelValue, props.orderId] as const,
  async ([method, orderId]) => {
    if (method === 'online' && orderId) {
      await nextTick();
      await mountPaymentDropin();
    } else if (method !== 'online' && checkoutDestroy) {
      checkoutDestroy();
      checkoutDestroy = null;
    }
  },
  { immediate: true },
);

onBeforeUnmount(() => {
  if (checkoutDestroy) {
    checkoutDestroy();
    checkoutDestroy = null;
  }
});
</script>

<style>
/* Provider CSS (e.g. Adyen Drop-in styles) is loaded by the checkout provider module */

/* Override payment provider styles to match the app's design tokens */
.adyen-dropin-wrapper {
  --adyen-sdk-color-background-primary: var(--card);
  --adyen-sdk-color-label-primary: var(--foreground);
  --adyen-sdk-color-label-secondary: var(--muted-foreground);
}

.adyen-dropin-wrapper .adyen-checkout__payment-method {
  border: 1px solid var(--border);
  border-radius: 0.5rem;
}
</style>
